const FORCED_MINIMUM_MULTIPLIER = 2;
const STALL_VISITOR_TYPE_ID = 'stall';

/**
 * @param {*} value
 * @return {string}
 */
function normalizeAddonKey(value) {
    return typeof value === 'string'
        ? value.trim()
        : '';
}

/**
 * @param {*} config
 * @return {{key: string}}
 */
function normalizeForcedMinimumAddonConfig(config) {
    return {
        key: normalizeAddonKey(config?.key),
    };
}

/**
 * @param {number} ticketCount
 * @return {number}
 */
function calculateForcedMinimumAddonQuantity(ticketCount) {
    const normalizedTicketCount = Number.isFinite(ticketCount)
        ? Math.max(0, Math.floor(ticketCount))
        : 0;

    return normalizedTicketCount * FORCED_MINIMUM_MULTIPLIER;
}

/**
 * @param {Array<{count?: number}>} tickets
 * @return {number}
 */
function sumTicketCounts(tickets) {
    if (Array.isArray(tickets) === false) {
        return 0;
    }

    return tickets.reduce((total, ticket) => {
        const count = parseInt(String(ticket?.count));

        return Number.isNaN(count)
            ? total
            : total + count;
    }, 0);
}

/**
 * @param {HTMLElement} domContainer
 * @return {number}
 */
function getSelectedTicketCount(domContainer) {
    return sumTicketCounts(getStallTicketInputs(domContainer).map(input => {
        return {count: input.value};
    }));
}

/**
 * @param {HTMLElement} domContainer
 * @return {HTMLInputElement[]}
 */
function getStallTicketInputs(domContainer) {
    if (!domContainer) {
        return [];
    }

    return Array.from(domContainer.querySelectorAll(`input[data-visitor-type-id="${STALL_VISITOR_TYPE_ID}"]`))
        .filter(input => input instanceof HTMLInputElement)
        .map(input => /** @type {HTMLInputElement} */ (input));
}

/**
 * @param {HTMLElement} domContainer
 * @param {{key: string}} config
 * @return {HTMLElement|null}
 */
function getForcedMinimumAddonContainer(domContainer, config) {
    if (!domContainer || !config.key) {
        return null;
    }

    const containers = Array.from(domContainer.querySelectorAll('[data-forced-minimum-addon="true"]'));

    return containers.find(container => normalizeAddonKey(container.dataset.addonKey) === config.key) || null;
}

/**
 * @param {{domContainer: HTMLElement, globalState: *}} options
 * @return {{changed: boolean, container: HTMLElement|null, minimum: number}}
 */
function applyForcedMinimumAddon(options) {
    const config = normalizeForcedMinimumAddonConfig(options?.globalState?.publicTicketConfig?.forcedMinimumAddon);
    const container = getForcedMinimumAddonContainer(options?.domContainer, config);

    if (!container) {
        return {changed: false, container: null, minimum: 0};
    }

    const checkbox = container.querySelector('input[type="checkbox"]');
    const countInput = container.querySelector('.addon-count-input');
    const subtractButton = container.querySelector('.subtract-button');
    const addButton = container.querySelector('.add-button');

    if (!checkbox || !countInput || !subtractButton || !addButton) {
        return {changed: false, container: null, minimum: 0};
    }

    const hasStallTicketInputs = getStallTicketInputs(options?.domContainer).length > 0;

    if (hasStallTicketInputs === false) {
        container.classList.add('d-none');
        checkbox.checked = false;
        countInput.value = '';
        subtractButton.disabled = true;

        return {changed: true, container, minimum: 0};
    }

    container.classList.remove('d-none');

    const minimum = calculateForcedMinimumAddonQuantity(getSelectedTicketCount(options?.domContainer));

    if (minimum < 1) {
        return {changed: false, container, minimum};
    }

    let changed = false;
    let currentCount = parseInt(String(countInput.value));

    if (Number.isNaN(currentCount)) {
        currentCount = 0;
    }

    if (checkbox.checked === false) {
        checkbox.checked = true;
        changed = true;
    }

    if (currentCount < minimum) {
        countInput.value = minimum.toFixed(0);
        currentCount = minimum;
        changed = true;
    }

    const shouldDisableSubtract = currentCount <= minimum;

    if (subtractButton.disabled !== shouldDisableSubtract) {
        subtractButton.disabled = shouldDisableSubtract;
    }

    return {changed, container, minimum};
}

export {
    applyForcedMinimumAddon,
    calculateForcedMinimumAddonQuantity,
    getSelectedTicketCount,
    normalizeForcedMinimumAddonConfig,
};
